import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  List,
  Popconfirm,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd'
import { DeleteOutlined, LinkOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons'

import {
  useApplyUserYandexLink,
  useCreateUserAuthIdentity,
  useCreateUserEmailAlias,
  useDeleteUserAuthIdentity,
  useDeleteUserEmailAlias,
  usePreviewUserYandexLink,
  useUnlinkUserYandexLink,
  useUpdateUserAuthIdentity,
  useUpdateUserEmailAlias,
  useUserAuthIdentities,
  useUserEmailAliases,
  useUserLoginReadiness,
} from '@/hooks/useUserIdentity'

const { Text } = Typography

interface UserIdentityTabProps {
  userId: string
  canonicalEmail: string
  zitadelId?: string | null
}

function isYandexExpectedEmail(email: string): boolean {
  const normalized = email.trim().toLowerCase()
  return normalized.endsWith('@yandex.ru') || normalized.endsWith('@ya.ru')
}

function renderBlockingIssue(issue: Record<string, unknown>): string {
  const reason = String(issue.reason || '')
  switch (reason) {
    case 'external_login_matches_other_active_user':
      return `Логин уже совпадает с local-part другого активного пользователя: ${issue.other_user_email || '—'}.`
    case 'external_login_already_linked_to_other_user':
      return `Этот live Yandex login уже привязан к другому пользователю: ${issue.other_user_email || '—'}.`
    case 'existing_auth_methods_require_manual_review':
      return 'У пользователя уже есть auth methods в Zitadel. Safe linking требует явного override.'
    case 'user_already_has_other_yandex_link':
      return `У пользователя уже есть другой live Yandex link: ${String(issue.current_yandex_logins || '')}.`
    case 'missing_zitadel_id':
      return 'У пользователя отсутствует zitadel_id.'
    case 'canonical_email_not_yandex':
      return 'Safe Yandex onboarding рассчитан только на канонический email @yandex.ru.'
    case 'user_is_inactive':
      return 'Пользователь деактивирован.'
    case 'empty_proposed_login':
      return 'Нужно указать внешний Yandex login.'
    default:
      return JSON.stringify(issue, null, 2)
  }
}

export default function UserIdentityTab({ userId, canonicalEmail, zitadelId }: UserIdentityTabProps) {
  const [aliasForm] = Form.useForm()
  const [identityForm] = Form.useForm()
  const [yandexAction, setYandexAction] = useState<'preview' | 'apply' | 'unlink' | null>(null)

  const { data: aliases, isLoading: aliasesLoading } = useUserEmailAliases(userId)
  const { data: identities, isLoading: identitiesLoading } = useUserAuthIdentities(userId)
  const { data: readiness, isLoading: readinessLoading, isFetching: readinessFetching } = useUserLoginReadiness(userId)

  const createAlias = useCreateUserEmailAlias()
  const updateAlias = useUpdateUserEmailAlias()
  const deleteAlias = useDeleteUserEmailAlias()

  const createIdentity = useCreateUserAuthIdentity()
  const updateIdentity = useUpdateUserAuthIdentity()
  const deleteIdentity = useDeleteUserAuthIdentity()
  const previewYandex = usePreviewUserYandexLink()
  const applyYandex = useApplyUserYandexLink()
  const unlinkYandex = useUnlinkUserYandexLink()

  const defaultYandexLogin = useMemo(() => canonicalEmail.trim().toLowerCase().split('@', 1)[0] || '', [canonicalEmail])
  const [yandexLogin, setYandexLogin] = useState(defaultYandexLogin)
  const [allowExistingAuthMethods, setAllowExistingAuthMethods] = useState(false)

  useEffect(() => {
    setYandexLogin(readiness?.yandex_login || defaultYandexLogin)
  }, [defaultYandexLogin, readiness?.yandex_login, userId])

  const latestYandexResult = unlinkYandex.data || applyYandex.data || previewYandex.data
  const yandexExpected = readiness?.yandex_expected ?? isYandexExpectedEmail(canonicalEmail)
  const currentYandexLogins = latestYandexResult?.current_yandex_logins || (readiness?.yandex_login ? [readiness.yandex_login] : [])
  const isMutatingYandex = yandexAction !== null
  const isRefreshingAfterYandexMutation = !isMutatingYandex && readinessFetching && (applyYandex.isSuccess || unlinkYandex.isSuccess)
  const isYandexBusy = isMutatingYandex || isRefreshingAfterYandexMutation

  const handleCreateAlias = async () => {
    const values = await aliasForm.validateFields()
    await createAlias.mutateAsync({ userId, data: values })
    aliasForm.resetFields()
  }

  const handleCreateIdentity = async () => {
    const values = await identityForm.validateFields()
    await createIdentity.mutateAsync({ userId, data: values })
    identityForm.resetFields()
  }

  const handlePreviewYandex = async () => {
    setYandexAction('preview')
    try {
      await previewYandex.mutateAsync({
        userId,
        data: {
          external_login: yandexLogin || null,
          allow_existing_auth_methods: allowExistingAuthMethods,
        },
      })
    } finally {
      setYandexAction(null)
    }
  }

  const handleApplyYandex = async () => {
    setYandexAction('apply')
    try {
      await applyYandex.mutateAsync({
        userId,
        data: {
          external_login: yandexLogin || null,
          allow_existing_auth_methods: allowExistingAuthMethods,
        },
      })
    } finally {
      setYandexAction(null)
    }
  }

  const handleUnlinkYandex = async () => {
    setYandexAction('unlink')
    try {
      await unlinkYandex.mutateAsync({
        userId,
        data: {
          external_login: yandexLogin || currentYandexLogins[0] || null,
        },
      })
    } finally {
      setYandexAction(null)
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title="Live login readiness">
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {readiness?.api_error ? (
            <Alert
              type="warning"
              showIcon
              message="Live status из Zitadel недоступен"
              description={readiness.api_error}
            />
          ) : null}

          <Space wrap>
            <Tag color={readiness?.yandex_linked ? 'green' : readiness?.yandex_expected ? 'red' : 'default'}>
              {readiness?.readiness_label || 'Проверяем статус...'}
            </Tag>
            {readiness?.yandex_expected ? <Tag color="blue">Ожидается Yandex SSO</Tag> : <Tag>Локальный / manual flow</Tag>}
            {readiness?.yandex_linked ? <Tag color="green">Yandex linked</Tag> : null}
            {readiness?.has_password_auth ? <Tag color="gold">Password auth</Tag> : null}
            {readiness?.auth_method_types?.length ? <Tag>{`${readiness.auth_method_types.length} auth method(s)`}</Tag> : null}
          </Space>

          <Text>{readiness?.readiness_note || 'Загружаем live auth status из Zitadel...'}</Text>

          <Space direction="vertical" size={2}>
            <Text type="secondary">Canonical email: <Text code>{canonicalEmail}</Text></Text>
            <Text type="secondary">Zitadel user: <Text code>{readiness?.zitadel_id || zitadelId || '—'}</Text></Text>
            <Text type="secondary">Yandex login: <Text code>{readiness?.yandex_login || '—'}</Text></Text>
          </Space>

          {(readiness?.warnings || []).map((warning) => (
            <Alert key={warning} type="warning" showIcon message={warning} />
          ))}

          {readinessLoading ? <Text type="secondary">Проверяем live login readiness...</Text> : null}
        </Space>
      </Card>

      {yandexExpected ? (
        <Card size="small" title="Yandex onboarding">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="Safe admin path для live Yandex link"
              description="Этот блок работает напрямую с live Zitadel links и guardrails. Он проверяет конфликты, уже существующие auth methods и не дает связать один и тот же Yandex login с разными пользователями."
            />

            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>Предлагаемый Yandex login</Text>
              <Input
                value={yandexLogin}
                disabled={isYandexBusy}
                onChange={(event) => setYandexLogin(event.target.value)}
                placeholder="local-part от yandex email"
              />
              <Text type="secondary">
                По умолчанию используется local-part canonical email. Для типового нового пользователя этого достаточно.
              </Text>
            </Space>

            <Space wrap>
              <Switch
                checked={allowExistingAuthMethods}
                disabled={isYandexBusy}
                onChange={setAllowExistingAuthMethods}
                checkedChildren="Override auth methods"
                unCheckedChildren="Safe mode"
              />
              <Text type="secondary">Разрешить linking, даже если в Zitadel уже есть auth methods.</Text>
            </Space>

            {isMutatingYandex ? (
              <Alert
                type="info"
                showIcon
                message={yandexAction === 'preview' ? 'Проверяем guardrails...' : 'Применяем изменения...'}
                description={yandexAction === 'preview'
                  ? 'Проверяем live Yandex link, конфликты и auth methods.'
                  : 'Изменение уже отправлено. Дождитесь обновления live статуса и списка сотрудников.'}
              />
            ) : null}

            {isRefreshingAfterYandexMutation ? (
              <Alert
                type="info"
                showIcon
                message="Обновляем live статус..."
                description="Новый статус уже применяется. Ждем завершения refetch, чтобы список сотрудников и карточка показали одно и то же состояние."
              />
            ) : null}

            <Space wrap>
              <Button loading={previewYandex.isPending} disabled={isYandexBusy} onClick={handlePreviewYandex}>
                Проверить linking
              </Button>
              <Button
                type="primary"
                loading={applyYandex.isPending}
                disabled={isYandexBusy}
                onClick={handleApplyYandex}
              >
                Связать Yandex
              </Button>
              <Button
                danger
                loading={unlinkYandex.isPending}
                disabled={isYandexBusy || !currentYandexLogins.length}
                onClick={handleUnlinkYandex}
              >
                Отвязать Yandex
              </Button>
            </Space>

            {latestYandexResult ? (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space wrap>
                  <Tag color={latestYandexResult.can_apply ? 'green' : latestYandexResult.status === 'manual_review_required' ? 'gold' : 'red'}>
                    {latestYandexResult.status_label}
                  </Tag>
                  {latestYandexResult.current_yandex_logins.length ? (
                    <Tag color="blue">{`Current link: ${latestYandexResult.current_yandex_logins.join(', ')}`}</Tag>
                  ) : (
                    <Tag>No live Yandex link</Tag>
                  )}
                  {latestYandexResult.current_auth_method_types.length ? (
                    <Tag>{`${latestYandexResult.current_auth_method_types.length} auth method(s)`}</Tag>
                  ) : null}
                </Space>

                <Text>{latestYandexResult.status_note}</Text>

                {latestYandexResult.api_error ? (
                  <Alert type="warning" showIcon message={latestYandexResult.api_error} />
                ) : null}

                {(latestYandexResult.warnings || []).map((warning) => (
                  <Alert key={warning} type="warning" showIcon message={warning} />
                ))}

                {(latestYandexResult.blocking_issues || []).map((issue, index) => (
                  <Alert
                    key={`${String(issue.reason || 'issue')}-${index}`}
                    type="error"
                    showIcon
                    message={renderBlockingIssue(issue)}
                  />
                ))}
              </Space>
            ) : null}
          </Space>
        </Card>
      ) : null}

      <Alert
        type="info"
        showIcon
        message="Login aliases и auth identities"
        description="Ниже находится control-plane слой. Для живой готовности входа ориентируйтесь на Live login readiness и Yandex onboarding выше: именно они показывают текущее состояние Zitadel links/auth methods."
      />

      <Card size="small" title="Canonical BI/RLS email">
        <Space direction="vertical" size={4}>
          <Text code>{canonicalEmail}</Text>
          <Text type="secondary">
            Этот email пока остается каноническим для BI/RLS. Login aliases не меняют его автоматически.
          </Text>
          {isYandexExpectedEmail(canonicalEmail) ? (
            <Alert
              type="warning"
              showIcon
              message="Yandex email сам по себе не включает Яндекс-вход автоматически"
              description="Для готовности BI/Yandex ориентируйтесь на блок Yandex onboarding выше. Сейчас это правило применяется к @yandex.ru и @ya.ru. Для кастомных доменов Yandex 360 нужен отдельный явный продуктовый признак, а не догадка по email."
            />
          ) : null}
        </Space>
      </Card>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={12}>
          <Card
            size="small"
            title={(
              <Space>
                <MailOutlined />
                <span>Email aliases</span>
              </Space>
            )}
          >
            <Form form={aliasForm} layout="vertical">
              <Form.Item
                name="email"
                label="Email для входа"
                rules={[{ required: true, type: 'email', message: 'Введите корректный email' }]}
              >
                <Input placeholder="user@example.com" />
              </Form.Item>
              <Space wrap style={{ marginBottom: 12 }}>
                <Form.Item name="is_verified" valuePropName="checked" initialValue={false} noStyle>
                  <Switch checkedChildren="Проверен" unCheckedChildren="Не проверен" />
                </Form.Item>
                <Form.Item name="is_login_enabled" valuePropName="checked" initialValue={false} noStyle>
                  <Switch checkedChildren="Login on" unCheckedChildren="Login off" />
                </Form.Item>
                <Form.Item name="is_primary_login" valuePropName="checked" initialValue={false} noStyle>
                  <Switch checkedChildren="Primary" unCheckedChildren="Secondary" />
                </Form.Item>
              </Space>
              <div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={createAlias.isPending}
                  onClick={handleCreateAlias}
                >
                  Добавить alias
                </Button>
              </div>
            </Form>

            <Divider />

            <List
              loading={aliasesLoading}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет aliases" /> }}
              dataSource={aliases || []}
              renderItem={(alias) => (
                <List.Item
                  actions={[
                    <Switch
                      key="verified"
                      size="small"
                      checked={Boolean(alias.verified_at)}
                      checkedChildren="verified"
                      unCheckedChildren="unverified"
                      loading={updateAlias.isPending}
                      onChange={(checked) => updateAlias.mutate({ userId, aliasId: alias.id, data: { is_verified: checked } })}
                    />,
                    <Switch
                      key="enabled"
                      size="small"
                      checked={alias.is_login_enabled}
                      checkedChildren="login on"
                      unCheckedChildren="login off"
                      loading={updateAlias.isPending}
                      onChange={(checked) => updateAlias.mutate({ userId, aliasId: alias.id, data: { is_login_enabled: checked } })}
                    />,
                    <Switch
                      key="primary"
                      size="small"
                      checked={alias.is_primary_login}
                      checkedChildren="primary"
                      unCheckedChildren="secondary"
                      loading={updateAlias.isPending}
                      onChange={(checked) => updateAlias.mutate({ userId, aliasId: alias.id, data: { is_primary_login: checked } })}
                    />,
                    <Popconfirm
                      key="delete"
                      title="Удалить login alias?"
                      onConfirm={() => deleteAlias.mutate({ userId, aliasId: alias.id })}
                    >
                      <Button danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={<Text code>{alias.email}</Text>}
                    description={(
                      <Space wrap>
                        {alias.is_primary_login ? <Tag color="gold">Primary</Tag> : null}
                        {alias.is_login_enabled ? <Tag color="green">Login enabled</Tag> : <Tag>Login disabled</Tag>}
                        {alias.verified_at ? <Tag color="blue">Verified</Tag> : <Tag>Unverified</Tag>}
                      </Space>
                    )}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card
            size="small"
            title={(
              <Space>
                <LinkOutlined />
                <span>Auth identities</span>
              </Space>
            )}
          >
            <Form form={identityForm} layout="vertical">
              <Form.Item name="provider" label="Provider" rules={[{ required: true, message: 'Укажите provider' }]}>
                <Input placeholder="zitadel:yandex / zitadel:vk" />
              </Form.Item>
              <Form.Item name="external_subject" label="External subject" rules={[{ required: true, message: 'Укажите subject' }]}>
                <Input placeholder="provider subject / sub" />
              </Form.Item>
              <Form.Item name="external_email" label="External email">
                <Input placeholder="user@example.com" />
              </Form.Item>
              <Form.Item name="external_login" label="External login">
                <Input placeholder="login / username" />
              </Form.Item>
              <Space wrap style={{ marginBottom: 12 }}>
                <Form.Item name="is_verified" valuePropName="checked" initialValue={false} noStyle>
                  <Switch checkedChildren="Проверен" unCheckedChildren="Не проверен" />
                </Form.Item>
                <Form.Item name="is_active" valuePropName="checked" initialValue={true} noStyle>
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Space>
              <div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={createIdentity.isPending}
                  onClick={handleCreateIdentity}
                >
                  Добавить identity
                </Button>
              </div>
            </Form>

            <Divider />

            <List
              loading={identitiesLoading}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет auth identities" /> }}
              dataSource={identities || []}
              renderItem={(identity) => (
                <List.Item
                  actions={[
                    <Switch
                      key="verified"
                      size="small"
                      checked={Boolean(identity.verified_at)}
                      checkedChildren="verified"
                      unCheckedChildren="unverified"
                      loading={updateIdentity.isPending}
                      onChange={(checked) => updateIdentity.mutate({ userId, identityId: identity.id, data: { is_verified: checked } })}
                    />,
                    <Switch
                      key="active"
                      size="small"
                      checked={identity.is_active}
                      checkedChildren="active"
                      unCheckedChildren="inactive"
                      loading={updateIdentity.isPending}
                      onChange={(checked) => updateIdentity.mutate({ userId, identityId: identity.id, data: { is_active: checked } })}
                    />,
                    <Popconfirm
                      key="delete"
                      title="Удалить auth identity?"
                      onConfirm={() => deleteIdentity.mutate({ userId, identityId: identity.id })}
                    >
                      <Button danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={<Space wrap><Text>{identity.provider}</Text><Text code>{identity.external_subject}</Text></Space>}
                    description={(
                      <Space direction="vertical" size={4}>
                        {identity.external_email ? <Text type="secondary">{identity.external_email}</Text> : null}
                        {identity.external_login ? <Text type="secondary">{identity.external_login}</Text> : null}
                        <Space wrap>
                          {identity.is_active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>}
                          {identity.verified_at ? <Tag color="blue">Verified</Tag> : <Tag>Unverified</Tag>}
                        </Space>
                      </Space>
                    )}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  )
}
